import {useMemo, useState} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {FEEDBACK_FORM_URL} from '#/lib/constants'
import {useOpenLink} from '#/lib/hooks/useOpenLink'
import {
  createFullHandle,
  MAX_SERVICE_HANDLE_LENGTH,
  validateServiceHandle,
} from '#/lib/strings/handles'
import {logger} from '#/logger'
import {
  checkHandleAvailability,
  useHandleAvailabilityQuery,
} from '#/state/queries/handle-availability'
import {Logomark} from '#/view/icons/Logomark'
import {useSignupContext} from '#/screens/Signup/state'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {useThrottledValue} from '#/components/hooks/useThrottledValue'
import {At_Stroke2_Corner0_Rounded as At} from '#/components/icons/At'
import {Check_Stroke2_Corner0_Rounded as Check} from '#/components/icons/Check'
import {
  AppBar,
  Eyebrow,
  FieldGroupCard,
  Footer,
  InputGroup,
  PrimaryButton,
  SelectionRow,
} from '#/components/onboarding-chrome'
import {Text} from '#/components/Typography'
import {useAnalytics} from '#/analytics'
import {HandleSuggestions} from './HandleSuggestions'

export function StepHandle({
  onPressSignIn,
}: {
  onPressSignIn?: (handle: string) => void
}) {
  const {_} = useLingui()
  const ax = useAnalytics()
  const t = useTheme()
  const openLink = useOpenLink()
  const {state, dispatch} = useSignupContext()
  const [draftValue, setDraftValue] = useState(state.handle)
  const [submitFoundTaken, setSubmitFoundTaken] = useState(false)
  const selectedDomain =
    state.userDomain ||
    state.serviceDescription?.availableUserDomains?.[0] ||
    ''
  const isNextLoading = useThrottledValue(state.isLoading, 500)

  const validCheck = validateServiceHandle(draftValue, selectedDomain)

  const {
    debouncedUsername: debouncedDraftValue,
    enabled: queryEnabled,
    query: {data: isHandleAvailable, isPending},
  } = useHandleAvailabilityQuery({
    username: draftValue,
    serviceDid: state.serviceDescription?.did ?? 'UNKNOWN',
    serviceDomain: selectedDomain,
    birthDate: state.dateOfBirth.toISOString(),
    email: state.email,
    enabled: validCheck.overall,
  })

  const hasDebounceSettled = draftValue === debouncedDraftValue
  const isHandleTaken =
    !isPending &&
    queryEnabled &&
    isHandleAvailable &&
    !isHandleAvailable.available
  const isNotReady = isPending || !hasDebounceSettled
  const isNextDisabled =
    !validCheck.overall || !!state.error || isNotReady ? true : isHandleTaken

  const showSignInInstead =
    !!onPressSignIn &&
    draftValue.length > 0 &&
    ((isHandleTaken && hasDebounceSettled) || submitFoundTaken)
  const trimmedDraft = draftValue.trim()
  const fullDraftHandle = createFullHandle(trimmedDraft, selectedDomain)

  const errorText = useMemo(() => {
    if (state.error) {
      return state.error
    }
    if (isHandleTaken && validCheck.overall) {
      return _(msg`${fullDraftHandle} is not available`)
    }
    if (draftValue.length === 0) {
      return undefined
    }
    if (!validCheck.hyphenStartOrEnd) {
      return _(msg`Username cannot begin or end with a hyphen`)
    }
    if (!validCheck.handleChars) {
      return _(
        msg`Username must only contain letters (a-z), numbers, and hyphens`,
      )
    }
    if (!validCheck.totalLength || !validCheck.frontLengthNotTooLong) {
      if (
        !validCheck.totalLength ||
        draftValue.length > MAX_SERVICE_HANDLE_LENGTH
      ) {
        return _(
          msg`Username cannot be longer than ${MAX_SERVICE_HANDLE_LENGTH} characters`,
        )
      }
      return _(msg`Username must be at least 3 characters`)
    }
    return undefined
  }, [state.error, isHandleTaken, validCheck, draftValue, fullDraftHandle, _])

  const supportingText =
    trimmedDraft.length > 0
      ? _(msg`Your username: @${fullDraftHandle}`)
      : undefined

  const onNextPress = async () => {
    const handle = draftValue.trim()

    dispatch({
      type: 'setHandle',
      value: handle,
    })
    dispatch({
      type: 'setUserDomain',
      value: selectedDomain,
    })

    if (!validCheck.overall) {
      return
    }

    dispatch({type: 'setIsLoading', value: true})

    try {
      const {available: handleAvailable} = await checkHandleAvailability(
        createFullHandle(handle, selectedDomain),
        state.serviceDescription?.did ?? 'UNKNOWN',
        {},
      )

      if (!handleAvailable) {
        setSubmitFoundTaken(true)
        dispatch({
          type: 'setError',
          value: _(msg`That handle is already taken.`),
          field: 'handle',
        })
        return
      }
    } catch (error) {
      logger.error('Failed to check handle availability on next press', {
        safeMessage: error,
      })
    } finally {
      dispatch({type: 'setIsLoading', value: false})
    }

    ax.metric('signup:nextPressed', {
      activeStep: state.activeStep,
      phoneVerificationRequired:
        state.serviceDescription?.phoneVerificationRequired,
    })

    if (!state.serviceDescription?.phoneVerificationRequired) {
      dispatch({
        type: 'submit',
        task: {verificationCode: undefined, mutableProcessed: false},
      })
      return
    }
    dispatch({type: 'next'})
  }

  const onBackPress = () => {
    const handle = draftValue.trim()
    dispatch({
      type: 'setHandle',
      value: handle,
    })
    dispatch({
      type: 'setUserDomain',
      value: selectedDomain,
    })
    dispatch({type: 'prev'})
    ax.metric('signup:backPressed', {activeStep: state.activeStep})
  }

  return (
    <View style={[a.flex_1, a.gap_lg]}>
      <AppBar
        showBack
        onBack={onBackPress}
        onHelp={() => openLink(FEEDBACK_FORM_URL({email: state.email}))}
      />

      <Eyebrow step={2} total={3} />

      <View style={[a.gap_xs]}>
        <Text style={[a.font_heading, a.text_3xl, a.leading_snug]}>
          <Trans>Create your profile</Trans>
        </Text>
        <Text
          style={[
            a.text_md,
            a.leading_snug,
            t.atoms.text,
            {fontWeight: '300', fontSize: 14, lineHeight: 22},
          ]}>
          <Trans>Choose your social experience.</Trans>
        </Text>
      </View>

      <FieldGroupCard>
        <InputGroup
          testID="handleInput"
          label={_(msg`Handle`)}
          icon={At}
          value={draftValue}
          placeholder={_(msg`Enter your handle`)}
          onChangeText={val => {
            if (state.error) {
              dispatch({type: 'setError', value: ''})
            }
            setSubmitFoundTaken(false)
            setDraftValue(val.toLowerCase())
          }}
          supportingText={supportingText}
          errorText={errorText}
          trailing={
            isHandleAvailable?.available && draftValue.length > 0 ? (
              <Check size="md" style={{color: t.palette.positive_600}} />
            ) : undefined
          }
          keyboardType="ascii-capable"
          autoCapitalize="none"
          autoCorrect={false}
          autoFocus
          autoComplete="off"
        />
      </FieldGroupCard>
      <View style={a.gap_sm}>
        {state.serviceDescription?.availableUserDomains
          .filter(
            domain =>
              state.selectedBrandSlug !== 'blacksky' ||
              ['blacksky.app', 'myatproto.social'].includes(
                domain.replace(/^\./, ''),
              ),
          )
          .sort(
            (left, right) =>
              Number(right.replace(/^\./, '') === 'blacksky.app') -
              Number(left.replace(/^\./, '') === 'blacksky.app'),
          )
          .map(domain => {
            const isBlacksky = domain.replace(/^\./, '') === 'blacksky.app'
            const description = isBlacksky
              ? _(msg`Open to the Black community`)
              : _(msg`Open to everyone`)
            return (
              <View
                key={domain}
                style={[
                  a.border,
                  a.rounded_sm,
                  t.atoms.border_contrast_medium,
                ]}>
                <SelectionRow
                  testID={`handleDomainOption-${domain.replace(/^\./, '')}`}
                  mode="radio"
                  emphasize
                  selected={domain === selectedDomain}
                  title={description}
                  subtitle={domain.replace(/^\./, '')}
                  onPress={() => {
                    dispatch({type: 'setUserDomain', value: domain})
                    dispatch({type: 'clearError'})
                    setSubmitFoundTaken(false)
                  }}
                  icon={
                    <View
                      style={[
                        a.align_center,
                        a.justify_center,
                        a.rounded_sm,
                        {
                          width: 40,
                          height: 40,
                          backgroundColor: isBlacksky ? '#080e0f' : '#ffffff',
                        },
                      ]}>
                      <Logomark
                        width={24}
                        fill={isBlacksky ? '#ffffff' : '#080e0f'}
                      />
                    </View>
                  }
                />
              </View>
            )
          })}
      </View>

      {isHandleTaken &&
        validCheck.overall &&
        isHandleAvailable?.suggestions &&
        isHandleAvailable.suggestions.length > 0 && (
          <HandleSuggestions
            suggestions={isHandleAvailable.suggestions}
            onSelect={suggestion => {
              const handlePart = suggestion.handle.includes('.')
                ? suggestion.handle.split('.')[0]
                : suggestion.handle.slice(0, selectedDomain.length * -1)
              setDraftValue(handlePart)
              ax.metric('signup:handleSuggestionSelected', {
                method: suggestion.method,
              })
            }}
          />
        )}

      {showSignInInstead && (
        <View style={[a.align_start]}>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            <Trans>Is this your account?</Trans>
          </Text>
          <Button
            testID="signInInsteadButton"
            label={_(msg`Sign in instead`)}
            variant="ghost"
            color="primary"
            size="small"
            style={[a.mt_xs]}
            onPress={() => onPressSignIn?.(fullDraftHandle)}>
            <ButtonText>
              <Trans>Sign in as {fullDraftHandle}</Trans>
            </ButtonText>
          </Button>
        </View>
      )}

      <Footer>
        <PrimaryButton
          testID="nextBtn"
          label={_(msg`Continue`)}
          onPress={onNextPress}
          disabled={isNextDisabled || isNextLoading}
        />
      </Footer>
    </View>
  )
}
